import React from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { Empty, Skeleton, Spin, Switch, Tag } from 'antd';
import { fetchArtifact } from '../../api/artifacts/client';
import { globalBus } from '../../context/event-bus';
import { injectArtifactAction } from '../../data/artifacts/logic';
import useArtifactsHook from '../../data/artifacts/useArtifactsHook';
import { NOTIFICATION_EMIT_EVENTS } from '../../data/notifications/logic';
import useSchemaHook from '../../data/schemas/useSchemaHook';
import styles from './ArtifactsTab.module.scss';
import DocumentView from './DocumentView';

interface Props {
  artifactId: string;
}

const ArtifactDetail: React.FC<Props> = ({ artifactId }) => {
  const intl = useIntl();
  const { dossierId } = useParams<{ dossierId: string }>();
  const dispatch = useDispatch();

  const [showCitations, setShowCitations] = React.useState(true);

  const [
    { artifacts, hasData: hasArtifacts, status: artifactsStatus },
    artifactsHandler,
  ] = useArtifactsHook(dossierId ?? '');
  const dispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasArtifacts || dispatchedRef.current === dossierId) {
      return;
    }
    dispatchedRef.current = dossierId;
    artifactsHandler.fetchArtifacts();
  }, [dossierId, hasArtifacts, artifactsHandler]);

  // A named artifact is filled asynchronously by the event agent, so refetch
  // when its generation event completes to swap the spinner for the document.
  React.useEffect(() => {
    if (!dossierId) {
      return;
    }
    const refetch = async (data: any) => {
      if (data?.dossierId !== dossierId) {
        return;
      }
      const id = (data?.metadata as { artifact_id?: string } | undefined)
        ?.artifact_id;
      const fresh = id
        ? await fetchArtifact(dossierId, id).catch(() => null)
        : null;
      if (fresh) {
        dispatch(injectArtifactAction(dossierId, fresh));
        return;
      }
      artifactsHandler.fetchArtifacts();
    };
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessed, refetch);
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, refetch);
    return () => {
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessed, refetch);
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, refetch);
    };
  }, [dossierId, artifactsHandler, dispatch]);

  const artifact = artifacts.find((a) => a.artifact_id === artifactId);
  const schemaName = artifact?.schema_name ?? '';
  const isAdhoc = artifact?.schema_name === 'adhoc';

  const [{ bundle, hasSchema, status: schemaStatus }, schemaHandler] =
    useSchemaHook(schemaName);
  const schemaDispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      isAdhoc ||
      !schemaName ||
      hasSchema ||
      schemaDispatchedRef.current === schemaName
    ) {
      return;
    }
    schemaDispatchedRef.current = schemaName;
    schemaHandler.fetchSchema();
  }, [isAdhoc, schemaName, hasSchema, schemaHandler]);

  const schema = isAdhoc
    ? (artifact.data_schema as Record<string, any> | null | undefined)
    : (bundle?.schema as Record<string, any> | undefined);
  const uiSchema = isAdhoc ? artifact.ui_schema : bundle?.ui_schema;

  const title =
    (schema?.title as string | undefined) ||
    (schemaName === 'adhoc' ? 'Unnamed' : schemaName) ||
    'Artifact';

  const artifactNotInDossier = hasArtifacts && !artifact;
  const artifactDataReady =
    !!artifact &&
    (!artifact.event_id || Object.keys(artifact.data ?? {}).length > 0);
  const ready =
    hasArtifacts && artifactDataReady && (isAdhoc ? !!schema : !!bundle);
  const failed =
    artifactNotInDossier ||
    artifactsStatus === 'rejected' ||
    (!isAdhoc && schemaStatus === 'rejected');
  const isStreaming =
    isAdhoc &&
    !artifact?.created_at &&
    Object.keys(artifact?.data ?? {}).length === 0;
  const isUpdating =
    isAdhoc &&
    !artifact?.created_at &&
    Object.keys(artifact?.data ?? {}).length > 0;
  // A named artifact is created empty and filled asynchronously by the event
  // agent, so an empty artifact with a linked generation event is still being
  // generated.
  const isGeneratingNamed =
    !isAdhoc &&
    !!artifact &&
    !!artifact.event_id &&
    Object.keys(artifact.data ?? {}).length === 0;
  const isGenerating = isStreaming || isUpdating || isGeneratingNamed;

  const isDocument = (uiSchema as any)?.renderer === 'document';

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.artifactLeft}>
          <div className={styles.titleBlock}>
            <h1 className={styles.artifactTitle}>{title}</h1>
            <div className={styles.statusRow}>
              {isGenerating && (
                <Tag
                  icon={<LoadingOutlined />}
                  className={styles.generatingTag}
                >
                  Generating
                </Tag>
              )}
              {artifact?.created_at && !isGenerating && (
                <span className={styles.createdTime}>
                  Created{' '}
                  {intl.formatDate(artifact.created_at, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
            {isDocument && (
              <div className={styles.controlsRow}>
                <label className={styles.controlToggle}>
                  <Switch
                    size="small"
                    checked={showCitations}
                    onChange={setShowCitations}
                  />
                  Show citations
                </label>
              </div>
            )}
          </div>
          {isStreaming ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : !ready && !failed ? (
            <div className={styles.loading}>
              <Spin />
            </div>
          ) : artifact && isDocument ? (
            <div className={styles.artifactMain}>
              <DocumentView
                uiSchema={uiSchema as any}
                data={artifact.data}
                showCitations={showCitations}
              />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtifactDetail;
